import * as React from "react";
import * as MenubarPrimitive from "@radix-ui/react-menubar";
import { CheckIcon, ChevronRightIcon, CircleIcon } from "lucide-react";

import { cn } from "@/lib/utils";

function Menubar({ className, ...props }: React.ComponentProps<typeof MenubarPrimitive.Root>) {
    return (
        <MenubarPrimitive.Root
            data-slot="menubar"
            className={cn("bg-background flex h-9 items-center gap-1 rounded-md !border p-1 shadow-xs", className)}
            {...props}
        />
    );
}

function MenubarMenu({ ...props }: React.ComponentProps<typeof MenubarPrimitive.Menu>) {
    return <MenubarPrimitive.Menu data-slot="menubar-menu" {...props} />;
}

function MenubarGroup({ ...props }: React.ComponentProps<typeof MenubarPrimitive.Group>) {
    return <MenubarPrimitive.Group data-slot="menubar-group" {...props} />;
}

function MenubarPortal({ ...props }: React.ComponentProps<typeof MenubarPrimitive.Portal>) {
    return <MenubarPrimitive.Portal data-slot="menubar-portal" {...props} />;
}

function MenubarRadioGroup({ ...props }: React.ComponentProps<typeof MenubarPrimitive.RadioGroup>) {
    return <MenubarPrimitive.RadioGroup data-slot="menubar-radio-group" {...props} />;
}

function MenubarTrigger({ className, ...props }: React.ComponentProps<typeof MenubarPrimitive.Trigger>) {
    return (
        <MenubarPrimitive.Trigger
            data-slot="menubar-trigger"
            className={cn(
                "setu:focus:bg-accent setu:focus:text-accent-foreground setu:data-[state=open]:bg-accent setu:data-[state=open]:text-accent-foreground setu:flex setu:items-center setu:rounded-sm setu:px-2 setu:py-1 setu:text-sm setu:font-medium setu:outline-hidden setu:select-none",
                className
            )}
            {...props}
        />
    );
}

function MenubarContent({
    className,
    align = "start",
    alignOffset = -4,
    sideOffset = 8,
    ...props
}: React.ComponentProps<typeof MenubarPrimitive.Content>) {
    return (
        <MenubarPortal>
            <MenubarPrimitive.Content
                data-slot="menubar-content"
                align={align}
                alignOffset={alignOffset}
                sideOffset={sideOffset}
                className={cn(
                    "setu:bg-popover setu:text-popover-foreground data-[state=open]:animate-in data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 setu:z-50 setu:min-w-[12rem] setu:origin-(--radix-menubar-content-transform-origin) setu:overflow-hidden setu:rounded-md setu:!border setu:p-1 setu:shadow-md",
                    className
                )}
                {...props}
            />
        </MenubarPortal>
    );
}

function MenubarItem({
    className,
    inset,
    variant = "default",
    ...props
}: React.ComponentProps<typeof MenubarPrimitive.Item> & {
    inset?: boolean;
    variant?: "default" | "destructive";
}) {
    return (
        <MenubarPrimitive.Item
            data-slot="menubar-item"
            data-inset={inset}
            data-variant={variant}
            className={cn(
                "setu:focus:bg-accent setu:focus:text-accent-foreground setu:data-[variant=destructive]:text-destructive setu:data-[variant=destructive]:focus:bg-destructive/10 dark:setu:data-[variant=destructive]:focus:bg-destructive/20 setu:data-[variant=destructive]:focus:text-destructive setu:data-[variant=destructive]:*:[svg]:!text-destructive setu:[&_svg:not([class*='text-'])]:text-muted-foreground setu:relative setu:flex setu:cursor-default setu:items-center setu:gap-2 setu:rounded-sm setu:px-2 setu:py-1.5 setu:text-sm setu:outline-hidden setu:select-none setu:data-[disabled]:pointer-events-none setu:data-[disabled]:opacity-50 setu:data-[inset]:pl-8 setu:[&_svg]:pointer-events-none setu:[&_svg]:shrink-0 setu:[&_svg:not([class*='size-'])]:size-4",
                className
            )}
            {...props}
        />
    );
}

function MenubarCheckboxItem({
    className,
    children,
    checked,
    ...props
}: React.ComponentProps<typeof MenubarPrimitive.CheckboxItem>) {
    return (
        <MenubarPrimitive.CheckboxItem
            data-slot="menubar-checkbox-item"
            className={cn(
                "setu:focus:bg-accent setu:focus:text-accent-foreground setu:relative setu:flex setu:cursor-default setu:items-center setu:gap-2 setu:rounded-xs setu:py-1.5 setu:pr-2 setu:pl-8 setu:text-sm setu:outline-hidden setu:select-none setu:data-[disabled]:pointer-events-none setu:data-[disabled]:opacity-50 setu:[&_svg]:pointer-events-none setu:[&_svg]:shrink-0 setu:[&_svg:not([class*='size-'])]:size-4",
                className
            )}
            checked={checked}
            {...props}
        >
            <span className="setu:pointer-events-none setu:absolute setu:left-2 setu:flex setu:size-3.5 setu:items-center setu:justify-center">
                <MenubarPrimitive.ItemIndicator>
                    <CheckIcon className="size-4" />
                </MenubarPrimitive.ItemIndicator>
            </span>
            {children}
        </MenubarPrimitive.CheckboxItem>
    );
}

function MenubarRadioItem({ className, children, ...props }: React.ComponentProps<typeof MenubarPrimitive.RadioItem>) {
    return (
        <MenubarPrimitive.RadioItem
            data-slot="menubar-radio-item"
            className={cn(
                "setu:focus:bg-accent setu:focus:text-accent-foreground setu:relative setu:flex setu:cursor-default setu:items-center setu:gap-2 setu:rounded-xs setu:py-1.5 setu:pr-2 setu:pl-8 setu:text-sm setu:outline-hidden setu:select-none setu:data-[disabled]:pointer-events-none setu:data-[disabled]:opacity-50 setu:[&_svg]:pointer-events-none setu:[&_svg]:shrink-0 setu:[&_svg:not([class*='size-'])]:size-4",
                className
            )}
            {...props}
        >
            <span className="setu:pointer-events-none setu:absolute setu:left-2 setu:flex setu:size-3.5 setu:items-center setu:justify-center">
                <MenubarPrimitive.ItemIndicator>
                    <CircleIcon className="setu:size-2 setu:fill-current" />
                </MenubarPrimitive.ItemIndicator>
            </span>
            {children}
        </MenubarPrimitive.RadioItem>
    );
}

function MenubarLabel({
    className,
    inset,
    ...props
}: React.ComponentProps<typeof MenubarPrimitive.Label> & {
    inset?: boolean;
}) {
    return (
        <MenubarPrimitive.Label
            data-slot="menubar-label"
            data-inset={inset}
            className={cn("setu:px-2 setu:py-1.5 setu:text-sm setu:font-medium setu:data-[inset]:pl-8", className)}
            {...props}
        />
    );
}

function MenubarSeparator({ className, ...props }: React.ComponentProps<typeof MenubarPrimitive.Separator>) {
    return (
        <MenubarPrimitive.Separator
            data-slot="menubar-separator"
            className={cn("setu:!bg-border setu:-mx-1 setu:my-1 setu:h-px", className)}
            {...props}
        />
    );
}

function MenubarShortcut({ className, ...props }: React.ComponentProps<"span">) {
    return (
        <span
            data-slot="menubar-shortcut"
            className={cn("setu:text-muted-foreground setu:ml-auto setu:text-xs setu:tracking-widest", className)}
            {...props}
        />
    );
}

function MenubarSub({ ...props }: React.ComponentProps<typeof MenubarPrimitive.Sub>) {
    return <MenubarPrimitive.Sub data-slot="menubar-sub" {...props} />;
}

function MenubarSubTrigger({
    className,
    inset,
    children,
    ...props
}: React.ComponentProps<typeof MenubarPrimitive.SubTrigger> & {
    inset?: boolean;
}) {
    return (
        <MenubarPrimitive.SubTrigger
            data-slot="menubar-sub-trigger"
            data-inset={inset}
            className={cn(
                "setu:focus:bg-accent setu:focus:text-accent-foreground setu:data-[state=open]:bg-accent setu:data-[state=open]:text-accent-foreground setu:flex setu:cursor-default setu:items-center setu:rounded-sm setu:px-2 setu:py-1.5 setu:text-sm setu:outline-none setu:select-none setu:data-[inset]:pl-8",
                className
            )}
            {...props}
        >
            {children}
            <ChevronRightIcon className="setu:ml-auto setu:h-4 setu:w-4" />
        </MenubarPrimitive.SubTrigger>
    );
}

function MenubarSubContent({ className, ...props }: React.ComponentProps<typeof MenubarPrimitive.SubContent>) {
    return (
        <MenubarPrimitive.SubContent
            data-slot="menubar-sub-content"
            className={cn(
                "setu:bg-popover setu:text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 setu:z-50 setu:min-w-[8rem] setu:origin-(--radix-menubar-content-transform-origin) setu:overflow-hidden setu:rounded-md setu:!border setu:p-1 setu:shadow-lg",
                className
            )}
            {...props}
        />
    );
}

export {
    Menubar,
    MenubarPortal,
    MenubarMenu,
    MenubarTrigger,
    MenubarContent,
    MenubarGroup,
    MenubarSeparator,
    MenubarLabel,
    MenubarItem,
    MenubarShortcut,
    MenubarCheckboxItem,
    MenubarRadioGroup,
    MenubarRadioItem,
    MenubarSub,
    MenubarSubTrigger,
    MenubarSubContent,
};
